import camelCase from 'lodash/camelCase';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';
import startCase from 'lodash/startCase';
import upperFirst from 'lodash/upperFirst';

import type { AdminListAuditLogsQuery } from '../../../graphql/types';

export type AuditLogRow = AdminListAuditLogsQuery['data']['nodes'][number];

type AuditPrincipal = AuditLogRow['principal'];

export type AuditPrincipalDisplay =
  | {
      kind: 'user';
      id: string;
      name: string;
    }
  | {
      kind: 'text';
      label: string;
    };

/** Dropped from ordering / diff logic (noise for readers). */
const OMIT_TIMESTAMPS = ['createdAt', 'updatedAt'];

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const IMPORT_APML_CATEGORIES = 'import_apml_categories';

function stringifyUnknownPretty(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** Who performed the action (user name + id link, or machine client label). */
export function getAuditLogPrincipalSummary(
  principal: AuditPrincipal,
): AuditPrincipalDisplay {
  switch (principal.__typename) {
    case 'User': {
      const name =
        `${principal.firstName ?? ''} ${principal.lastName ?? ''}`.trim();
      return {
        kind: 'user',
        id: principal.id,
        name: name || principal.id,
      };
    }
    case 'MachineClient':
      return { kind: 'text', label: `MachineClient (${principal.id})` };
    default:
      return { kind: 'text', label: 'Unknown' };
  }
}

/** Pretty-print for `<pre>` fallback blocks. */
export function formatAuditLogJson(value: unknown): string {
  if (value === undefined || value === null) {
    return '—';
  }
  return stringifyUnknownPretty(value);
}

/** Single-line or fallback text (not resource links). */
export function formatAuditLogValueForDisplay(value: unknown): string {
  if (value === undefined || value === null) {
    return '—';
  }
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }
  return stringifyUnknownPretty(value);
}

/**
 * Maps audit actions like `create_exercise_base` to the React Admin resource name (`ExerciseBase`)
 * used for the primary record link.
 */
export function getAdminResourceNameForAuditAction(
  action: string,
): string | null {
  const prefix = action.startsWith('create_')
    ? 'create_'
    : action.startsWith('update_')
      ? 'update_'
      : null;
  if (!prefix) {
    return null;
  }
  const rest = action.slice(prefix.length);
  if (!rest) {
    return null;
  }
  return upperFirst(camelCase(rest));
}

/** Turns a camelCase field key into a short title (e.g. update diff rows). */
export function formatAuditLogFieldKeyAsLabel(key: string): string {
  return startCase(key);
}

/** Labels for fields on create snapshots (`id` / `name` get fixed titles). */
export function getAuditLogFieldLabelForCreateEntry(key: string): string {
  if (key === 'id') {
    return 'ID';
  }
  if (key === 'name') {
    return 'Name';
  }
  return formatAuditLogFieldKeyAsLabel(key);
}

/**
 * For `*Id` foreign keys, returns the React Admin resource name when it maps to a
 * currently registered resource (e.g. `exerciseTypeId` → `ExerciseType`,
 * `clientId` → `Client`). `registeredResources` is the set of resource names the
 * Admin actually exposes, so links never point at a resource that isn't routable.
 */
export function getAdminResourceForForeignKeyField(
  key: string,
  registeredResources: ReadonlySet<string>,
): string | null {
  if (!key.endsWith('Id')) {
    return null;
  }
  const base = key.slice(0, -2);
  if (!base) {
    return null;
  }
  const candidate = upperFirst(base);
  return registeredResources.has(candidate) ? candidate : null;
}

/**
 * When a cell should open a resource show page, returns `{ resource, id }`;
 * otherwise callers show {@link formatAuditLogValueForDisplay}.
 */
export function getResourceLinkTargetForAuditValue(
  value: unknown,
  fieldKey: string,
  primaryEntityResource: string | undefined,
  registeredResources: ReadonlySet<string>,
): { resource: string; id: string } | undefined {
  if (typeof value !== 'string' || !UUID_RE.test(value)) {
    return undefined;
  }
  if (
    fieldKey === 'id' &&
    primaryEntityResource &&
    registeredResources.has(primaryEntityResource)
  ) {
    return { resource: primaryEntityResource, id: value };
  }
  const fk = getAdminResourceForForeignKeyField(fieldKey, registeredResources);
  if (fk) {
    return { resource: fk, id: value };
  }
  return undefined;
}

/** Keys that differ between `previousData` and `updatedData`, excluding timestamp-only noise. */
export function collectChangedFieldsFromAuditUpdate(
  previousData: Record<string, unknown>,
  updatedData: Record<string, unknown>,
): Array<{ key: string; from: unknown; to: unknown }> {
  const updatedWithoutTimestamps = omit(updatedData, OMIT_TIMESTAMPS) as Record<
    string,
    unknown
  >;
  const out: Array<{ key: string; from: unknown; to: unknown }> = [];
  for (const key of Object.keys(updatedWithoutTimestamps)) {
    const newVal = updatedWithoutTimestamps[key];
    const oldVal = previousData[key];
    if (!isEqual(oldVal, newVal)) {
      out.push({ key, from: oldVal, to: newVal });
    }
  }
  return sortBy(out, 'key');
}

/**
 * Field order for create entries: `id`, `name`, then remaining keys A–Z
 * (timestamps excluded).
 */
export function getOrderedFieldKeysForAuditCreateEntry(
  data: Record<string, unknown>,
): string[] {
  const keys = Object.keys(omit(data, OMIT_TIMESTAMPS));
  const rest = keys
    .filter((k) => k !== 'id' && k !== 'name')
    .sort((a, b) => a.localeCompare(b));
  const ordered: string[] = [];
  if (keys.includes('id')) {
    ordered.push('id');
  }
  if (keys.includes('name')) {
    ordered.push('name');
  }
  ordered.push(...rest);
  return ordered;
}
